import { makeStyles } from '@material-ui/core';
import { CUT_OFF_DISTANCE } from 'doc-mate-store/lib/constants/location';
import { useStores } from 'doc-mate-store/lib/hooks';
import { GPSPositionData, LatLng } from 'doc-mate-store/lib/models/GPSPosition';
import { distanceBetween } from 'geofire-common';
import GoogleMapReact, { Coords } from 'google-map-react';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';
import config from '../../config';
import { LoadStopWithFlag } from '../../containers/Track';
import { colors } from '../../themes';
import ArrowMarker from './ArrowMarker';
import GenericMarker from './GenericMarker';
import TruckMarker from './TruckMarker';
import TruckPath from './TruckPath';

type Props = {
  className?: string;
  defaultCenter?: Coords;
  defaultZoom?: number;
  driverPath: GPSPositionData[];
  driverPosition?: GPSPositionData;
  stops?: LoadStopWithFlag[] | undefined;
  route?: LatLng[];
  showRoute?: boolean;
  selectedStop1: string;
  selectedStop2: string;
  noReload?: boolean;
};

const DEFAULT_CENTER: Coords = { lat: 42.281389, lng: -83.748333 }; // Ann Harbor, MI
const DEFAULT_ZOOM = 11;

const RouteMap: React.FC<Props> = ({
  className,
  defaultCenter,
  defaultZoom,
  driverPath,
  driverPosition,
  stops,
  route,
  selectedStop1,
  selectedStop2,
  showRoute = false,
  noReload = false,
}) => {
  const [mapRef, setMapRef] = useState<any>();
  const [mapsRef, setMapsRef] = useState<any>();
  const classes = useStyles();
  const center = defaultCenter || DEFAULT_CENTER;
  const zoom = defaultZoom || DEFAULT_ZOOM;
  const { rootStore } = useStores();
  const { me } = rootStore;
  const user = me ? me.maybeCurrent : undefined;
  const isDispatcherOrBroker = !!(user && (user.isDispatcher || user.isBroker));

  const [drawn, setDrawn] = useState(false);

  function onGoogleApiLoaded(map: any, maps: any) {
    setMapRef(map);
    setMapsRef(maps);
  }

  // function toLetters(num: number): string {
  //   var mod = num % 26,
  //     pow = (num / 26) | 0,
  //     out = mod ? String.fromCharCode(64 + mod) : (--pow, 'Z');
  //   return pow ? toLetters(pow) + out : out;
  // }

  useEffect(() => {
    if (mapRef && mapsRef && !noReload) {
      const bounds = new mapsRef.LatLngBounds();
      let waypoints: { location: any; stopover: boolean }[] = [];

      if (route) {
        route.forEach(coords => {
          const { latitude, longitude } = coords;
          const latLng = new mapsRef.LatLng(latitude, longitude);
          bounds.extend(latLng);
          waypoints.push({
            location: latLng,
            stopover: false,
          });
        });
      }

      let directionsRenderer: any;
      if (waypoints.length > 1) {
        const origin = waypoints[0].location;
        const destination = waypoints[waypoints.length - 1].location;
        waypoints = waypoints.slice(1, waypoints.length - 1);

        const request = {
          origin,
          waypoints,
          destination,
          travelMode: mapsRef.TravelMode.DRIVING,
          avoidTolls: true,
          avoidFerries: true,
        };

        const directionsRendererPolyline = {
          strokeColor: colors.primary[300],
          strokeOpacity: 0.8,
          strokeWeight: 8,
        };
        const directionsService = new mapsRef.DirectionsService();
        directionsRenderer = new mapsRef.DirectionsRenderer({
          polylineOptions: directionsRendererPolyline,
          suppressMarkers: true,
          preserveViewport: true,
        });
        directionsRenderer.setMap(mapRef);
        if (showRoute) {
          directionsService.route(
            request,
            (result: unknown, status: string) => {
              if (status === 'OK') {
                directionsRenderer.setDirections(result);
              }
            },
          );
        }
      }

      if (!drawn) {
        mapRef.fitBounds(bounds);
        setDrawn(true);
      }

      // return () => {
      //   if (directionsRenderer) {
      //     directionsRenderer.setMap(null);
      //   }
      // };
    }
  }, [mapRef, mapsRef, drawn, route, showRoute, noReload]);

  const truckPath = useMemo(() => {
    let samplePaths: any[] = [];
    driverPath
      .sort((a, b) => {
        if (a.loadId && b.loadId) {
          if (a.loadId === b.loadId) {
            return a.timestamp - b.timestamp;
          } else {
            return a.loadId - b.loadId;
          }
        }
        return 1;
      })
      .forEach((p, index) => {
        if (index === 0) {
          samplePaths.push([p]);
        } else {
          if (p.loadId !== driverPath[index - 1].loadId) {
            samplePaths.push([p]);
          } else {
            if (
              !p.firstGps &&
              distanceBetween(
                [
                  driverPath[index - 1].latitude,
                  driverPath[index - 1].longitude,
                ],
                [p.latitude, p.longitude],
              ) > CUT_OFF_DISTANCE
            ) {
              samplePaths.push([p]);
            } else {
              samplePaths[samplePaths.length - 1].push(p);
            }
          }
        }
      });

    return samplePaths;
  }, [driverPath]);

  // const updateDriverPath = useMemo(() => {
  //   let samplePaths: any[][] = [];
  //   const driverPathLength = driverPath.length;
  //   let i = 0;

  //   if (driverPathLength < 10) {
  //     samplePaths.push(driverPath);
  //     return samplePaths;
  //   }

  //   while (i * 9 < driverPathLength) {
  //     if ((i + 1) * 9 < driverPathLength) {
  //       if (i) {
  //         samplePaths[i] = driverPath.slice(i * 9, (i + 1) * 9 + 1); //9 - 18, 18 -27
  //       } else {
  //         samplePaths[i] = driverPath.slice(0, 10); //0-9
  //       }
  //     } else {
  //       samplePaths[i] = driverPath.slice(i * 9, driverPathLength);
  //     }
  //     i += 1;
  //   }

  //   return samplePaths;
  // }, [driverPath]);

  // useEffect(() => {
  //   if (isDispatcherOrBroker) {
  //     if (mapRef && mapsRef && updateDriverPath) {
  //       updateDriverPath.forEach((path: GPSPositionData[], index: number) => {
  //         let waypoints: { location: any; stopover: boolean }[] = [];

  //         path.forEach((p: GPSPositionData, index: number) => {
  //           const { latitude, longitude } = p;
  //           const latLng = new mapsRef.LatLng(latitude, longitude);

  //           waypoints.push({
  //             location: latLng,
  //             stopover: false,
  //           });
  //         });
  //         let directionsRenderer: any;
  //         if (waypoints.length > 1) {
  //           const origin = waypoints[0].location;
  //           const destination = waypoints[waypoints.length - 1].location;
  //           waypoints = waypoints.slice(1, waypoints.length - 1);

  //           const request = {
  //             origin,
  //             waypoints,
  //             destination,
  //             travelMode: mapsRef.TravelMode.DRIVING,
  //           };

  //           const directionsRendererPolyline = {
  //             strokeColor: colors.primary[800],
  //             strokeOpacity: 0.8,
  //             strokeWeight: 8,
  //           };
  //           const directionsService = new mapsRef.DirectionsService();
  //           directionsRenderer = new mapsRef.DirectionsRenderer({
  //             polylineOptions: directionsRendererPolyline,
  //             suppressMarkers: true,
  //             preserveViewport: true,
  //           });
  //           directionsRenderer.setMap(mapRef);

  //           directionsService.route(
  //             request,
  //             (result: unknown, status: string) => {
  //               if (status === 'OK') {
  //                 directionsRenderer.setDirections(result);
  //               }
  //             },
  //           );
  //         }

  //         return () => {
  //           if (directionsRenderer) {
  //             directionsRenderer.setMap(null);
  //           }
  //         };
  //       });
  //     }
  //   }
  // }, [
  //   mapRef,
  //   mapsRef,
  //   drawn,
  //   route,
  //   showRoute,
  //   updateDriverPath,
  //   isDispatcherOrBroker,
  // ]);

  const mapOptions = {
    mapTypeControl: true,
  };

  return (
    <div className={[classes.root, className ? className : ''].join(' ')}>
      <GoogleMapReact
        bootstrapURLKeys={config.googleMaps.bootstrapURLKeys}
        defaultCenter={center}
        defaultZoom={zoom}
        // layerTypes={['TrafficLayer']}
        options={mapOptions}
        onGoogleApiLoaded={({ map, maps }) => onGoogleApiLoaded(map, maps)}
        yesIWantToUseGoogleMapApiInternals
      >
        {mapRef && driverPosition && (
          <TruckMarker
            key={driverPosition.timestamp}
            heading={driverPosition.heading}
            lat={driverPosition.latitude}
            lng={driverPosition.longitude}
            map={mapRef}
          />
        )}
        {mapRef &&
          mapsRef &&
          truckPath &&
          truckPath.map((path: any, index) => (
            <TruckPath
              key={`truckPath-${index}`}
              maps={mapsRef}
              map={mapRef}
              path={path}
            />
          ))}

        {isDispatcherOrBroker &&
          mapRef &&
          mapsRef &&
          driverPath.map((path, index) => (
            <ArrowMarker
              key={`${path.timestamp}-${index}`}
              path={path}
              lat={path.latitude}
              lng={path.longitude}
              map={mapRef}
            />
          ))}

        {mapRef &&
          mapsRef &&
          stops &&
          stops.map((stop, index) => (
            <GenericMarker
              key={`stop-${index}`}
              lat={stop.loadStop.latlng.latitude}
              lng={stop.loadStop.latlng.longitude}
              stopType={stop.loadStop.stopType}
              isPickupDropoff={stop.isPickupDropff}
              stopNumber={index + 1}
              isSelected={
                selectedStop1 === stop.loadStop.location.id ||
                selectedStop2 === stop.loadStop.location.id
              }
            />
          ))}
      </GoogleMapReact>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
}));

export default observer(RouteMap);
